import { useEffect } from 'react'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router'
import { trackPage } from './lib/telemetry'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAdoptDemoStaffToken } from './lib/demoStaffToken'
import { AdminLayout, OwnerOnly } from './routes/AdminLayout'
import { CataloguePage } from './routes/admin/CataloguePage'
import { EnquiriesPage } from './routes/admin/EnquiriesPage'
import { PullSheetPage } from './routes/admin/PullSheetPage'
import { SettingsPage } from './routes/admin/SettingsPage'
import { StorefrontPage } from './routes/admin/StorefrontPage'
import { SyncPage } from './routes/admin/SyncPage'
import { ThemePage } from './routes/admin/ThemePage'
import { VisitListPage } from './routes/admin/VisitListPage'
import { FollowUpsPage } from './routes/admin/FollowUpsPage'
import { NewOrderPage } from './routes/admin/NewOrderPage'
import { OrderDetailPage } from './routes/admin/OrderDetailPage'
import { InvoicePage } from './routes/admin/InvoicePage'
import { JovitaOrdersPage } from './routes/admin/JovitaOrdersPage'
import { OrdersPage } from './routes/admin/OrdersPage'
import { PaymentsPage } from './routes/admin/PaymentsPage'
import { CommissionPage } from './routes/admin/CommissionPage'
import { AlertsPage } from './routes/admin/AlertsPage'
import { CustomerHomeRoute } from './routes/CustomerHomeRoute'
import { BookedRoute } from './routes/BookedRoute'
import { HoldRoute, ReserveDoneRoute, ReserveRoute } from './routes/ReserveRoute'
import { SharedRoomRoute } from './routes/SharedRoomRoute'
import { SessionRoute } from './routes/SessionRoute'
import { StaffSignInRoute } from './routes/StaffSignInRoute'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // A customer will leave this open on a phone for a week; a stale rail is
      // worse than a refetch.
      staleTime: 30_000,
    },
  },
})

/**
 * Inside the query provider because that is where the hook can run, and rendered
 * as nothing because adopting a credential is not a visual concern.
 */
function DemoStaffToken() {
  useAdoptDemoStaffToken()
  return null
}

/** One analytics page view per route change — scrubbed of tokens, and none for the staff console (`lib/telemetry`). */
function PageViews() {
  const { pathname } = useLocation()
  useEffect(() => trackPage(pathname), [pathname])
  return null
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DemoStaffToken />
        <PageViews />
        {/*
          Two entrances, and they are not the same page.

          `/` and `/start` are the customer's; `/staff` and everything under
          `/admin` are the boutique's. They used to be one route — `/` was
          `DevIndexRoute`, which offered a bride a "start a fitting" button and,
          under it, a staff token field, the day's visit list and a box for
          pasting other people's session tokens. That page is gone.

          `*` falls to the customer's home rather than to a 404 because an
          unknown path here is overwhelmingly a mistyped or truncated customer
          link, and the home page is the one screen that can tell her where a
          working link comes from.
        */}
        <Routes>
          <Route path="/" element={<CustomerHomeRoute />} />
          {/* The same door under the name a stylist can be told, and the one
              the console links to. See `CustomerHomeRoute`. */}
          <Route path="/start" element={<CustomerHomeRoute />} />
          <Route path="/staff" element={<StaffSignInRoute />} />
          <Route path="/s/:token" element={<SessionRoute />} />
          {/* Booking (docs/23): the card on file, Koalendar, and back. And a fitting room shared read-only. */}
          <Route path="/reserve" element={<ReserveRoute />} />
          <Route path="/reserve/done" element={<ReserveDoneRoute />} />
          <Route path="/hold" element={<HoldRoute />} />
          <Route path="/booked" element={<BookedRoute />} />
          <Route path="/v/:shareToken" element={<SharedRoomRoute />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<VisitListPage />} />
            <Route path="visits/:visitId" element={<PullSheetPage />} />
            <Route path="follow-ups" element={<FollowUpsPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/new" element={<NewOrderPage />} />
            <Route path="orders/jovita" element={<JovitaOrdersPage />} />
            <Route path="orders/:orderId" element={<OrderDetailPage />} />
            <Route path="orders/:orderId/invoice" element={<InvoicePage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="enquiries" element={<EnquiriesPage />} />
            <Route path="catalogue" element={<CataloguePage />} />
            {/* The two owner areas. `OwnerOnly` draws the refusal for a
                stylist who arrived by URL rather than by tab; the endpoints
                behind both pages refuse her too. */}
            <Route path="payments" element={<OwnerOnly><PaymentsPage /></OwnerOnly>} />
            <Route path="commission" element={<OwnerOnly><CommissionPage /></OwnerOnly>} />
            <Route path="sync" element={<OwnerOnly><SyncPage /></OwnerOnly>} />
            <Route path="storefront" element={<OwnerOnly><StorefrontPage /></OwnerOnly>} />
            <Route path="theme" element={<OwnerOnly><ThemePage /></OwnerOnly>} />
            <Route path="settings" element={<OwnerOnly><SettingsPage /></OwnerOnly>} />
          </Route>
          <Route path="*" element={<CustomerHomeRoute />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
